import { App } from 'antd';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { assistantApi, useCreateAssistantMutation } from '@entities/assistant';
import { quickPicks, useModels, type LlmModel } from '@entities/llm';
import { useUserStore } from '@entities/user';
import { clearDraft, readDraft, writeDraft, type CreateDraft } from '@features/create-assistant';
import { extractErrorMessage } from '@shared/api';
import { rub } from '@shared/ui/v2';
import './create.css';

function ModelCard({
  m,
  tag,
  tagColor,
  on,
  onPick,
}: {
  m: LlmModel;
  tag: string;
  tagColor?: string;
  on: boolean;
  onPick: () => void;
}) {
  const price = m.price_per_answer_rub ?? m.price_per_answer_estimate_rub;
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      className={on ? 'cr-choice is-on' : 'cr-choice'}
      onClick={onPick}
    >
      <span className="cr-tag" style={tagColor ? { color: tagColor } : undefined}>
        {tag}
      </span>
      <span style={{ fontWeight: 700 }}>{m.title}</span>
      <span className="v2-small" style={{ color: '#374151' }}>
        {m.eval_score != null ? `${Math.round(m.eval_score)}% верных · ` : ''}
        {price != null ? `${m.price_per_answer_rub == null ? '≈ ' : ''}${rub(price)} за ответ` : ''}
      </span>
    </button>
  );
}

/** Экран 2 «Новый ассистент» (утверждён 28.10.2026). Отдельная страница без левого меню. */
export function CreateAssistantPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const { data: models } = useModels();
  const create = useCreateAssistantMutation();
  const [d, setD] = useState<CreateDraft>(readDraft);
  const [composing, setComposing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nameError, setNameError] = useState(false);

  useEffect(() => writeDraft(d), [d]);
  const set = <K extends keyof CreateDraft>(k: K, v: CreateDraft[K]) =>
    setD((x) => ({ ...x, [k]: v }));

  const picks = quickPicks(models ?? []);
  const defModel = picks.def;
  const modelId = d.modelId ?? defModel?.id ?? null;
  const cards: Array<{ m: LlmModel; tag: string; tagColor?: string }> = [];
  if (defModel) {
    cards.push({
      m: defModel,
      tag: defModel.data_in_russia ? 'ПО УМОЛЧАНИЮ · ДАННЫЕ В РОССИИ' : 'ПО УМОЛЧАНИЮ',
      tagColor: '#B45309',
    });
  }
  if (picks.cheapest && !cards.some((c) => c.m.id === picks.cheapest!.id))
    cards.push({ m: picks.cheapest, tag: 'ВЫГОДНО' });
  if (picks.accurate && !cards.some((c) => c.m.id === picks.accurate!.id))
    cards.push({ m: picks.accurate, tag: 'ТОЧНЕЕ ВСЕХ' });
  // Модель из сравнения встаёт третьей карточкой — карточек всегда три (экран 2).
  const chosen = models?.find((m) => m.id === modelId);
  if (chosen && !cards.some((c) => c.m.id === chosen.id))
    cards.splice(Math.min(cards.length, 2), 1, { m: chosen, tag: 'ВЫБРАНА' });

  // «Новому аккаунту — 1 000 ₽ на тесты»: только тем, кто бонус ещё не получал (экран 2).
  // Бонус начисляется при регистрации, поэтому строку показываем лишь при пустом балансе
  // и без ассистентов — у таких аккаунтов бонус ещё не начислялся (старые аккаунты до V2-1).
  const firstAssistant = (user?.assistants?.length ?? 0) === 0 && Number(user?.balance ?? 0) <= 0;
  const [promptShown, setPromptShown] = useState(!!d.prompt);

  const compose = async () => {
    if (!d.name.trim()) {
      setNameError(true);
      message.info('Сначала впишите название');
      return;
    }
    if (d.description.trim().length < 3) {
      message.info('Опишите в паре фраз, чем занимается компания');
      return;
    }
    setComposing(true);
    try {
      const prompt = await assistantApi.composeInstruction({
        name: d.name.trim(),
        description: d.description.trim(),
        model_id: modelId ?? undefined,
      });
      set('prompt', prompt);
      setPromptShown(true);
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setComposing(false);
    }
  };

  const submit = async () => {
    if (!d.name.trim()) {
      setNameError(true);
      document.getElementById('name')?.focus();
      return;
    }
    setBusy(true);
    try {
      let prompt = d.prompt.trim();
      // Не нажали «Составить инструкцию» — составляем из описания сами (экран 2).
      if (!prompt && d.description.trim().length >= 3) {
        try {
          prompt = await assistantApi.composeInstruction({
            name: d.name.trim(),
            description: d.description.trim(),
            model_id: modelId ?? undefined,
          });
        } catch {
          prompt = d.description.trim();
        }
      }
      const created = await create.mutateAsync({
        name: d.name.trim(),
        ...(prompt ? { prompt } : {}),
        ...(modelId ? { model_id: modelId } : {}),
      });
      clearDraft();
      const next =
        d.kb === 'files'
          ? `/assistants/${created.id}/knowledge?upload=1`
          : d.kb === 'site'
            ? `/assistants/${created.id}/knowledge?site=1`
            : `/assistants/${created.id}/test-chat`;
      navigate(next, { replace: true });
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // «Отмена» — туда, откуда пришли; по умолчанию Главная (экран 2).
  const cancel = () => {
    clearDraft();
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="cr-page">
      <header className="cr-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <Link to="/" className="v2-logo" style={{ fontSize: 18 }} onClick={() => clearDraft()}>
            RICHAT
          </Link>
          <span className="v2-muted">Новый ассистент</span>
        </div>
        <button type="button" className="cr-cancel" onClick={cancel}>
          Отмена
        </button>
      </header>

      <main className="cr-main">
        <div className="cr-wrap">
          <div>
            <h1 className="cr-h1">Создадим ассистента</h1>
            <p className="v2-muted" style={{ margin: '4px 0 0' }}>
              Три шага, около двух минут. Всё можно поменять потом.
            </p>
          </div>

          <div role="radiogroup" aria-label="Что создаём" className="cr-types">
            <button type="button" role="radio" aria-checked className="cr-type is-on">
              <span className="cr-type-icon" style={{ background: '#EFF6FF', color: '#2563EB' }}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 5h16v11H8l-4 4z" />
                </svg>
              </span>
              <span>
                <span style={{ display: 'block', fontWeight: 700 }}>Ассистент для клиентов</span>
                <span className="v2-small v2-muted" style={{ display: 'block' }}>
                  Отвечает по вашим документам на сайте, в Telegram и MAX
                </span>
              </span>
            </button>
            <Link to="/api-access" className="cr-type">
              <span className="cr-type-icon" style={{ background: '#F3F4F6' }}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M8 7l-5 5 5 5M16 7l5 5-5 5" />
                </svg>
              </span>
              <span>
                <span style={{ display: 'block', fontWeight: 700 }}>Доступ по API</span>
                <span className="v2-small v2-muted" style={{ display: 'block' }}>
                  300+ ИИ моделей для ваших задач. База знаний не обязательна
                </span>
              </span>
            </Link>
          </div>

          <section aria-labelledby="s1" className="cr-card">
            <h2 id="s1" className="cr-h2">
              <span className="v2-muted">1.</span> Название и задача
            </h2>
            <div className="cr-field">
              <label htmlFor="name">Название</label>
              <input
                id="name"
                type="text"
                value={d.name}
                maxLength={200}
                aria-invalid={nameError}
                className={nameError ? 'cr-input is-error' : 'cr-input'}
                placeholder="Например, Сервисный центр «КофеСервис»"
                onChange={(e) => {
                  set('name', e.target.value);
                  setNameError(false);
                }}
              />
              {nameError && (
                <span className="cr-error">
                  Впишите название — так ассистента будет видно в кабинете
                </span>
              )}
            </div>
            <div className="cr-field">
              <label htmlFor="task">Чем занимается компания и что должен делать ассистент</label>
              <textarea
                id="task"
                rows={3}
                className="cr-input cr-textarea"
                maxLength={4000}
                value={d.description}
                placeholder="Например: ремонтируем кофемашины. Нужно отвечать клиентам про цены, гарантию и сроки и записывать на диагностику."
                onChange={(e) => set('description', e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
              <button type="button" className="cr-btn" onClick={compose} disabled={composing}>
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
                </svg>
                {composing ? 'Составляем…' : d.prompt ? 'Составить заново' : 'Составить инструкцию'}
              </button>
              <span className="v2-small v2-muted">
                ИИ напишет инструкцию по вашему описанию — её можно поправить.
              </span>
            </div>
            {promptShown && (
              <div className="cr-field">
                <label htmlFor="prompt">
                  Инструкция — поправьте, если нужно (потом она в «Настройке»)
                </label>
                <textarea
                  id="prompt"
                  rows={7}
                  className="cr-input cr-textarea"
                  value={d.prompt}
                  onChange={(e) => set('prompt', e.target.value)}
                />
              </div>
            )}
          </section>

          <section aria-labelledby="s2" className="cr-card">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 8,
              }}
            >
              <h2 id="s2" className="cr-h2">
                <span className="v2-muted">2.</span> Модель
              </h2>
              <Link to="/assistants/new/model" className="v2-link">
                Сравнить все модели
              </Link>
            </div>
            <div role="radiogroup" aria-label="Модель" className="cr-choices">
              {cards.map((c) => (
                <ModelCard
                  key={c.m.id}
                  m={c.m}
                  tag={c.tag}
                  tagColor={c.tagColor}
                  on={c.m.id === modelId}
                  onPick={() => set('modelId', c.m.id)}
                />
              ))}
            </div>
            <div className="v2-small v2-muted">
              Зарубежные модели обрабатывают вопросы клиентов за пределами России. Если это важно
              для вашего бизнеса — выберите модель с меткой «Данные в России».
            </div>
          </section>

          <section aria-labelledby="s3" className="cr-card">
            <h2 id="s3" className="cr-h2">
              <span className="v2-muted">3.</span> База знаний{' '}
              <span className="v2-small v2-muted" style={{ fontWeight: 500 }}>
                — можно позже
              </span>
            </h2>
            <div role="radiogroup" aria-label="База знаний" className="cr-choices">
              {[
                { id: 'files' as const, t: 'Загрузить файлы', h: 'Инструкции, прайсы, договоры' },
                { id: 'site' as const, t: 'Подключить сайт', h: 'Возьмём страницы с вашего сайта' },
                {
                  id: 'skip' as const,
                  t: 'Пропустить',
                  h: 'Ассистент будет отвечать только по инструкции',
                },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={d.kb === o.id}
                  className={d.kb === o.id ? 'cr-choice is-on' : 'cr-choice'}
                  onClick={() => set('kb', o.id)}
                >
                  <span style={{ fontWeight: 600 }}>{o.t}</span>
                  <span className="v2-small v2-muted">{o.h}</span>
                </button>
              ))}
            </div>
            {(d.kb === 'files' || d.kb === 'site') && (
              <div className="v2-small v2-muted">
                После создания откроется «База знаний»: цену загрузки покажем до списания.
              </div>
            )}
          </section>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14 }}>
            <button type="button" className="cr-submit" onClick={submit} disabled={busy}>
              {busy
                ? 'Создаём…'
                : d.kb === 'files'
                  ? 'Создать и загрузить файлы'
                  : d.kb === 'site'
                    ? 'Создать и подключить сайт'
                    : 'Создать и открыть тест-чат'}
            </button>
            {firstAssistant && (
              <span className="v2-small v2-muted">Новому аккаунту — 1 000 ₽ на тесты</span>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
